/**
 * Registry of route path → actual components ON that page.
 * NO pattern matching. Each route explicitly declares its components.
 * Add new routes here when you want them available for holdco views.
 */
export type RouteComponentEntry = {
  key: string;
  label: string;
  category: string;
};

export const ROUTE_COMPONENTS_REGISTRY: Record<string, RouteComponentEntry[]> = {
  // /dashboard - main Dashboard page (useDashboardConfig)
  '/dashboard': [
    { key: 'team_avatars', label: 'Team Avatars', category: 'header' },
    { key: 'team_on_vacation', label: 'Team on Vacation', category: 'header' },
    { key: 'fte_tasks_list', label: 'FTE Tasks List', category: 'activity' },
    { key: 'upcoming_appointments', label: 'Upcoming Appointments', category: 'calendar' },
    { key: 'active_brands', label: 'Active Brands', category: 'metrics' },
    { key: 'tasks_due_today', label: 'Tasks Due Today', category: 'metrics' },
    { key: 'tasks_completed_today', label: 'Tasks Completed Today', category: 'metrics' },
    { key: 'leads_summary_today', label: 'Leads (Today)', category: 'insights' },
    { key: 'leads_summary_last_7_days', label: 'Leads (Last 7 days)', category: 'insights' },
    { key: 'leads_summary_last_14_days', label: 'Leads (Last 14 days)', category: 'insights' },
    { key: 'leads_summary_last_month', label: 'Leads (Last month)', category: 'insights' },
    { key: 'leads_summary_this_month', label: 'Leads (This month)', category: 'insights' },
    { key: 'leads_daily_trends', label: 'Leads Daily Trends', category: 'insights' },
    { key: 'leads_channels', label: 'Leads Channels', category: 'insights' },
    { key: 'leads_medium', label: 'Leads Medium', category: 'insights' },
    { key: 'leads_sources', label: 'Leads Sources', category: 'insights' },
    { key: 'conversion_funnel_today', label: 'Conversion Funnel (Today)', category: 'insights' },
    { key: 'conversion_funnel_last_7_days', label: 'Conversion Funnel (Last 7 days)', category: 'insights' },
    { key: 'conversion_funnel_last_14_days', label: 'Conversion Funnel (Last 14 days)', category: 'insights' },
    { key: 'conversion_funnel_last_month', label: 'Conversion Funnel (Last month)', category: 'insights' },
    { key: 'conversion_funnel_this_month', label: 'Conversion Funnel (This month)', category: 'insights' },
    { key: 'project_progress', label: 'Growth Playbook Progress', category: 'insights' },
    { key: 'project_progress_score', label: 'Progress Score', category: 'insights' },
    { key: 'team_activity_feed', label: 'Team Activity Feed', category: 'activity' },
    { key: 'tasks_assigned_grid', label: 'Tasks Assigned Grid', category: 'activity' },
    { key: 'visuals_to_be_approved', label: 'Visuals to be Approved', category: 'activity' },
    { key: 'weekly_calendar_view', label: 'Weekly Calendar View', category: 'calendar' },
    { key: 'quota_overview', label: 'Monthly Quotas', category: 'quota' },
  ],

  // /analytics - AnalyticsPage (GA data)
  '/analytics': [
    { key: 'analytics_sessions', label: 'Sessions', category: 'metrics' },
    { key: 'analytics_users', label: 'Users', category: 'metrics' },
    { key: 'analytics_pageviews', label: 'Page Views', category: 'metrics' },
    { key: 'analytics_bounce_rate', label: 'Bounce Rate', category: 'metrics' },
    { key: 'analytics_avg_session_duration', label: 'Avg Session Duration', category: 'metrics' },
    { key: 'analytics_true_traffic', label: 'True Traffic (Users)', category: 'metrics' },
    { key: 'analytics_traffic_trends_chart', label: 'Traffic Trends Chart', category: 'insights' },
    { key: 'analytics_device_split_chart', label: 'Device Split Chart', category: 'insights' },
    { key: 'analytics_traffic_drivers_chart', label: 'Traffic Drivers Chart', category: 'insights' },
    { key: 'analytics_top_pages_table', label: 'Top-20 Pages Table', category: 'insights' },
    { key: 'analytics_traffic_sources_table', label: 'Traffic Sources Table', category: 'insights' },
    { key: 'analytics_top_referrers_table', label: 'Top-20 Referrers Table', category: 'insights' },
    { key: 'analytics_hostname_table', label: 'Traffic by Hostname Table', category: 'insights' },
    { key: 'analytics_session_sources_table', label: 'Top-20 Session Sources Table', category: 'insights' },
    { key: 'analytics_session_medium_table', label: 'Top-20 Session Medium Table', category: 'insights' },
    { key: 'analytics_traffic_countries_grid', label: 'Traffic Countries Grid', category: 'insights' },
  ],

  // /seo-report - SeoReportPage (GSC data)
  '/seo-report': [
    { key: 'seo_report_query_counting', label: 'Query Counting Card', category: 'metrics' },
    { key: 'seo_report_branded_vs_nonbranded', label: 'Branded vs Non-Branded', category: 'metrics' },
    { key: 'seo_report_gsc_monthly_chart', label: 'GSC Monthly Chart', category: 'insights' },
    { key: 'seo_report_top_pages', label: 'Top Pages Table', category: 'insights' },
    { key: 'seo_report_top_queries', label: 'Top Queries Table', category: 'insights' },
    { key: 'seo_report_countries', label: 'Countries Pie Chart', category: 'insights' },
    { key: 'seo_report_devices', label: 'Devices Donut Chart', category: 'insights' },
    { key: 'seo_report_declines', label: 'Decline Analysis', category: 'insights' },
    { key: 'seo_report_increases', label: 'Increase Analysis', category: 'insights' },
  ],

  // /seo-dashboard - SeoDashboardPage
  '/seo-dashboard': [
    { key: 'seo_dashboard_overview_cards', label: 'SEO Overview Cards', category: 'metrics' },
    { key: 'seo_dashboard_stats_cards', label: 'SEO Stats Cards', category: 'metrics' },
    { key: 'seo_dashboard_keyword_sources', label: 'Keyword Sources Cards', category: 'insights' },
    { key: 'seo_dashboard_on_page_section', label: 'On-Page SEO Section', category: 'insights' },
    { key: 'seo_dashboard_top_countries', label: 'Top Countries Card', category: 'insights' },
    { key: 'seo_dashboard_keyword_opportunities', label: 'Keyword Opportunities Chart', category: 'insights' },
    { key: 'seo_dashboard_recent_activity', label: 'Recent Activity Card', category: 'activity' },
  ],

  // /scale-2y3x/dashboard - Scale2Y3XDashboard
  '/scale-2y3x/dashboard': [
    { key: 'scale_2y3x_team_avatars', label: 'Team Avatars', category: 'header' },
    { key: 'scale_2y3x_goals_card', label: 'Goals Card', category: 'metrics' },
    { key: 'scale_2y3x_growth_lab_team_card', label: 'Growth Lab Team Card', category: 'metrics' },
    { key: 'scale_2y3x_active_sprints_card', label: 'Active Sprints Card', category: 'metrics' },
    { key: 'scale_2y3x_ideas_card', label: 'Ideas Card', category: 'metrics' },
    { key: 'scale_2y3x_flagged_ideas', label: 'Flagged Ideas to Revisit', category: 'activity' },
    { key: 'scale_2y3x_time_progress', label: 'Time Progress', category: 'insights' },
    { key: 'scale_2y3x_qualitative_task_progress', label: 'Qualitative Task Progress', category: 'insights' },
    { key: 'scale_2y3x_goal_progress', label: 'Goal Progress', category: 'insights' },
    { key: 'scale_2y3x_team_members_list', label: 'Team Members List', category: 'activity' },
  ],

  // /sales-insights - SalesInsights page (proposal analytics)
  '/sales-insights': [
    { key: 'sales_insights_total_proposals', label: 'Total Proposals', category: 'metrics' },
    { key: 'sales_insights_draft', label: 'Draft Proposals', category: 'metrics' },
    { key: 'sales_insights_responded', label: 'Responded Proposals', category: 'metrics' },
    { key: 'sales_insights_awarded', label: 'Awarded Proposals', category: 'metrics' },
    { key: 'sales_insights_total_value_awarded', label: 'Total Value Awarded', category: 'metrics' },
    { key: 'sales_insights_total_value', label: 'Total Value', category: 'metrics' },
    { key: 'sales_insights_avg_views_per_proposal', label: 'Avg Views per Proposal', category: 'metrics' },
    { key: 'sales_insights_avg_views_per_awarded', label: 'Avg Views per Awarded', category: 'metrics' },
    { key: 'sales_insights_avg_deal_value_awarded', label: 'Avg $ Deal Awarded', category: 'metrics' },
    { key: 'sales_insights_avg_sales_cycle', label: 'Avg Sales Cycle', category: 'metrics' },
    { key: 'sales_insights_weighted_avg_deal', label: 'Weighted Avg $ Deal', category: 'metrics' },
    { key: 'sales_insights_awarded_vs_responded_chart', label: 'Awarded vs Responded Chart', category: 'insights' },
    { key: 'sales_insights_responded_vs_total_chart', label: 'Responded vs Total Chart', category: 'insights' },
    { key: 'sales_insights_proposals_table', label: 'Proposals Table', category: 'insights' },
  ],

  // /contacts - AllContactsPage (os_contacts hub)
  '/contacts': [
    { key: 'contacts_total_contacts', label: 'Total Contacts', category: 'metrics' },
    { key: 'contacts_total_organizations', label: 'Total Organizations', category: 'metrics' },
    { key: 'contacts_total_modules', label: 'Total Modules', category: 'metrics' },
    { key: 'contacts_total_activities', label: 'Total Activities', category: 'metrics' },
    { key: 'contacts_module_filters', label: 'Module Filters', category: 'header' },
    { key: 'contacts_activity_filters', label: 'Activity Filters', category: 'header' },
    { key: 'contacts_search', label: 'Search', category: 'header' },
    { key: 'contacts_table', label: 'Contacts Table', category: 'activity' },
  ],

  // /admin/user-activity-tracking - UserActivityTrackingPage
  '/admin/user-activity-tracking': [
    { key: 'user_activity_filters', label: 'Brand / User / Date Filters', category: 'header' },
    { key: 'user_activity_total_time', label: 'Total Time', category: 'metrics' },
    { key: 'user_activity_total_visits', label: 'Total Visits', category: 'metrics' },
    { key: 'user_activity_unique_routes', label: 'Unique Routes', category: 'metrics' },
    { key: 'user_activity_unique_users', label: 'Unique Users', category: 'metrics' },
    { key: 'user_activity_over_time_chart', label: 'Activity Over Time', category: 'insights' },
    { key: 'user_activity_time_by_brand_chart', label: 'Time by Brand', category: 'insights' },
    { key: 'user_activity_records_table', label: 'Activity Records Table', category: 'activity' },
  ],

  // /growth-plan - GrowthPlanPage (Day Start / Day End standup)
  '/growth-plan': [
    { key: 'growth_standup_day_start', label: 'Day Start', category: 'activity' },
    { key: 'growth_standup_day_end', label: 'Day End', category: 'activity' },
    { key: 'growth_standup_today_summary', label: "Today's Standup Summary", category: 'metrics' },
    { key: 'growth_standup_day_start_only', label: 'Day Start (separate)', category: 'activity' },
    { key: 'growth_standup_day_end_only', label: 'Day End (separate)', category: 'activity' },
  ],

  // /user-settings - UserSettingsPage (favorites, languages, themes only)
  '/user-settings': [
    { key: 'user_settings_favorites', label: 'Favorites', category: 'activity' },
    { key: 'user_settings_languages', label: 'Languages', category: 'metrics' },
    { key: 'user_settings_themes', label: 'Themes', category: 'metrics' },
  ],

  // /lms/ask-for-more - LearnAdminAskForMore
  '/lms/ask-for-more': [
    { key: 'lms_ask_for_more_all', label: 'All Requests', category: 'metrics' },
    { key: 'lms_ask_for_more_asked', label: 'Asked', category: 'metrics' },
    { key: 'lms_ask_for_more_confirmed', label: 'Confirmed', category: 'metrics' },
    { key: 'lms_ask_for_more_planned', label: 'Planned', category: 'metrics' },
    { key: 'lms_ask_for_more_rejected', label: 'Rejected', category: 'metrics' },
    { key: 'lms_ask_for_more_search', label: 'Search', category: 'header' },
    { key: 'lms_ask_for_more_requests_table', label: 'Content Requests Table', category: 'activity' },
  ],

  // /lms/dashboard - LearnAdminDashboard
  '/lms/dashboard': [
    { key: 'lms_dashboard_total_courses', label: 'Total Courses', category: 'metrics' },
    { key: 'lms_dashboard_published', label: 'Published', category: 'metrics' },
    { key: 'lms_dashboard_draft', label: 'Draft', category: 'metrics' },
    { key: 'lms_dashboard_companies_enrolled', label: 'Companies Enrolled', category: 'metrics' },
    { key: 'lms_dashboard_students_enrolled', label: 'Students Enrolled', category: 'metrics' },
    { key: 'lms_dashboard_currently_watching', label: 'Currently Watching', category: 'activity' },
    { key: 'lms_dashboard_course_tags', label: 'Course Tags', category: 'insights' },
    { key: 'lms_dashboard_lesson_tags', label: 'Lesson Tags', category: 'insights' },
    { key: 'lms_dashboard_top_student', label: 'Top Student', category: 'insights' },
    { key: 'lms_dashboard_top_company', label: 'Top Company', category: 'insights' },
    { key: 'lms_dashboard_top_lesson', label: 'Top Lesson', category: 'insights' },
    { key: 'lms_dashboard_worst_performing_lesson', label: 'Worst Performing Lesson', category: 'insights' },
    { key: 'lms_dashboard_lesson_most_claps', label: 'Lesson Most Claps', category: 'insights' },
    { key: 'lms_dashboard_student_most_claps', label: 'Student Most Claps', category: 'insights' },
    { key: 'lms_dashboard_lesson_least_claps', label: 'Lesson Least Claps', category: 'insights' },
    { key: 'lms_dashboard_student_least_claps', label: 'Student Least Claps', category: 'insights' },
  ],

  // /admin/user-time-tracking - UserTimeTrackingPage
  '/admin/user-time-tracking': [
    { key: 'user_time_filters', label: 'Brand / User / Date / Entry Type Filters', category: 'header' },
    { key: 'user_time_total_minutes', label: 'Total Time', category: 'metrics' },
    { key: 'user_time_foundational', label: 'Foundational', category: 'metrics' },
    { key: 'user_time_growth', label: 'Growth', category: 'metrics' },
    { key: 'user_time_recurring', label: 'Recurring', category: 'metrics' },
    { key: 'user_time_logged_over_time_chart', label: 'Time Logged Over Time', category: 'insights' },
    { key: 'user_time_by_task_type_chart', label: 'By Task Type', category: 'insights' },
    { key: 'user_time_by_source_chart', label: 'By Source', category: 'insights' },
    { key: 'user_time_by_brand_chart', label: 'By Brand', category: 'insights' },
    { key: 'user_time_entries_table', label: 'Time Entries Table', category: 'activity' },
  ],

  // /newsroom - NewsroomDashboard (src/pages/newsroom/NewsroomDashboard.tsx)
  '/newsroom': [
    { key: 'newsroom_programs_card', label: 'Programs', category: 'metrics' },
    { key: 'newsroom_sources_card', label: 'RSS Sources', category: 'metrics' },
    { key: 'newsroom_sponsorships_card', label: 'Sponsorships', category: 'metrics' },
    { key: 'newsroom_recent_bulletins', label: 'Recent Bulletins', category: 'insights' },
  ],

  // /newsfeed/leads - NewsfeedLeadsPage (src/pages/newsfeed/NewsfeedLeadsPage.tsx)
  '/newsfeed/leads': [
    { key: 'newsfeed_leads_total_subscribers', label: 'Total Subscribers', category: 'metrics' },
    { key: 'newsfeed_leads_active', label: 'Active', category: 'metrics' },
    { key: 'newsfeed_leads_unsubscribed', label: 'Unsubscribed', category: 'metrics' },
    { key: 'newsfeed_leads_filters', label: 'Search & Filters', category: 'header' },
    { key: 'newsfeed_leads_table', label: 'Leads Table', category: 'activity' },
  ],

  // /podcasthost - PodcastHostDashboard (src/pages/podcast-host/PodcastHostDashboard.tsx)
  '/podcasthost': [
    { key: 'podcasthost_active_shows', label: 'Active Shows', category: 'metrics' },
    { key: 'podcasthost_upcoming_episodes', label: 'Upcoming Episodes', category: 'metrics' },
    { key: 'podcasthost_pending_confirmations', label: 'Pending Confirmations', category: 'metrics' },
    { key: 'podcasthost_active_campaigns', label: 'Active Campaigns', category: 'metrics' },
    { key: 'podcasthost_upcoming_recordings', label: 'Upcoming Recordings', category: 'insights' },
    { key: 'podcasthost_needs_attention', label: 'Needs Attention', category: 'activity' },
    { key: 'podcasthost_quick_actions', label: 'Quick Actions', category: 'activity' },
  ],

  // /personas-dashboard - PersonasDashboardPage (src/pages/PersonasDashboardPage.tsx)
  '/personas-dashboard': [
    { key: 'personas_total_personas', label: 'Total Personas', category: 'metrics' },
    { key: 'personas_briefs', label: 'Personas Briefs', category: 'metrics' },
    { key: 'personas_job_roles', label: 'Job Roles', category: 'metrics' },
    { key: 'personas_average_age', label: 'Average Age', category: 'metrics' },
    { key: 'personas_filters', label: 'Filters', category: 'header' },
    { key: 'personas_list', label: 'Personas List', category: 'activity' },
  ],

  // /fg-dashboard - FocusGroupsDashboardPage (src/pages/FocusGroupsDashboardPage.tsx)
  '/fg-dashboard': [
    { key: 'fg_total_projects', label: 'Total Projects', category: 'metrics' },
    { key: 'fg_focus_groups_30d', label: 'Focus Groups (30d)', category: 'metrics' },
    { key: 'fg_total_sessions', label: 'Total Sessions', category: 'metrics' },
    { key: 'fg_total_responses', label: 'Total Responses', category: 'metrics' },
    { key: 'fg_sessions_by_category', label: 'Sessions by Category', category: 'insights' },
    { key: 'fg_resonance_chart', label: 'What Resonated vs What Didn\'t', category: 'insights' },
    { key: 'fg_emotional_reactions', label: 'Emotional Reactions', category: 'insights' },
    { key: 'fg_authenticity_chart', label: 'Authentic vs Pushy Language', category: 'insights' },
  ],

  // /events - EventsDashboard (src/pages/events/EventsDashboard.tsx)
  '/events': [
    { key: 'events_total_events', label: 'Total Events', category: 'metrics' },
    { key: 'events_upcoming', label: 'Upcoming', category: 'metrics' },
    { key: 'events_pending_leads', label: 'Pending Leads', category: 'metrics' },
    { key: 'events_active_campaigns', label: 'Active Campaigns', category: 'metrics' },
    { key: 'events_upcoming_list', label: 'Upcoming Events', category: 'insights' },
    { key: 'events_quick_actions', label: 'Quick Actions', category: 'activity' },
  ],

  // Add more routes here as needed. Each route = the actual components on that page.
};

/** Normalize route for lookup: leading slash, no trailing slash, trim. */
function normalizeRoute(route: string): string {
  const s = (route || '').trim();
  if (!s) return '/';
  const withSlash = s.startsWith('/') ? s : `/${s}`;
  return withSlash.endsWith('/') && withSlash.length > 1 ? withSlash.slice(0, -1) : withSlash;
}

/** Get components for a route. Returns empty array if route not in registry. */
export function getComponentsForRouteFromRegistry(route: string): RouteComponentEntry[] {
  const normalized = normalizeRoute(route);
  return ROUTE_COMPONENTS_REGISTRY[normalized] ?? [];
}

/** Get routes that contain a given component key. Used for route badges in Configure Dashboard. */
export function getRoutesForComponent(componentKey: string): string[] {
  return Object.entries(ROUTE_COMPONENTS_REGISTRY)
    .filter(([, components]) => components.some((c) => c.key === componentKey))
    .map(([route]) => route);
}

/** All registered route paths. */
export const REGISTERED_ROUTES = Object.keys(ROUTE_COMPONENTS_REGISTRY).sort();

/**
 * Map route path → page file path. Used to generate the "add route" command for unregistered routes.
 * Add entries here when you add new routes to the app.
 */
export const ROUTE_TO_PAGE_MAP: Record<string, string> = {
  '/lms/dashboard': 'src/pages/LearnAdminDashboard.tsx',
  '/lms/ask-for-more': 'src/pages/LearnAdminAskForMore.tsx',
  '/lms/analytics': 'src/pages/LearnAdminAnalytics.tsx',
  '/causality-report': 'src/pages/CausalityReportPage.tsx',
  '/email-marketing-report': 'src/pages/EmailMarketingReportPage.tsx',
  '/product_report': 'src/pages/ProductReportPage.tsx',
  '/link-building/analytics': 'src/pages/LinkBuildingAnalyticsPage.tsx',
  '/cold-outreach/analytics': 'src/pages/ColdOutreachAnalyticsPage.tsx',
  '/documentation-hub/dashboard': 'src/pages/DocumentationHubDashboardPage.tsx',
  '/predictive-analytics': 'src/pages/PredictiveAnalyticsPage.tsx',
  '/seo-analytics': 'src/pages/SeoAnalyticsPage.tsx',
  '/personas-dashboard': 'src/pages/PersonasDashboardPage.tsx',
  '/fg-dashboard': 'src/pages/FocusGroupsDashboardPage.tsx',
  '/gh-dashboard': 'src/pages/GrowthHackingDashboardPage.tsx',
  '/saio-dashboard': 'src/pages/SaioDashboardPage.tsx',
  '/sop-dashboard': 'src/pages/SopDashboardPage.tsx',
  '/performance-marketing-dashboard': 'src/pages/PerformanceMarketingDashboardPage.tsx',
  '/sales-insights': 'src/pages/SalesOperations/SalesInsights.tsx',
  '/contacts': 'src/pages/AllContactsPage.tsx',
  '/referral/contacts': 'src/pages/referral/ReferralContactsPage.tsx',
  '/referral/programs': 'src/pages/referral/ReferralProgramsPage.tsx',
  '/referral/campaigns': 'src/pages/referral/ReferralCampaignsPage.tsx',
  '/referral/dispatch': 'src/pages/referral/ReferralDispatchPage.tsx',
  '/referral/prompts': 'src/pages/referral/ReferralPromptsPage.tsx',
  '/referral/analytics': 'src/pages/referral/ReferralAnalyticsPage.tsx',
  '/admin/user-activity-tracking': 'src/pages/admin/UserActivityTrackingPage.tsx',
  '/admin/user-time-tracking': 'src/pages/admin/UserTimeTrackingPage.tsx',
  '/growth-plan': 'src/pages/GrowthPlanPage.tsx',
  '/user-settings': 'src/pages/UserSettingsPage.tsx',
  '/newsroom': 'src/pages/newsroom/NewsroomDashboard.tsx',
  '/newsfeed/leads': 'src/pages/newsfeed/NewsfeedLeadsPage.tsx',
  '/podcasthost': 'src/pages/podcast-host/PodcastHostDashboard.tsx',
  '/events': 'src/pages/events/EventsDashboard.tsx',
};

/** Get the copy-paste command to add an unregistered route. */
export function getAddRouteCommand(route: string): string {
  const normalized = normalizeRoute(route);
  const pagePath = ROUTE_TO_PAGE_MAP[normalized];
  const registryPath = 'src/config/routeComponentsRegistry.ts';
  if (pagePath) {
    return `Add route ${normalized} to ${registryPath} using components from ${pagePath}`;
  }
  return `Add route ${normalized} to ${registryPath} using components from src/pages/[PAGE_FILE].tsx`;
}
